import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStats, getOrders, getVapidPublicKey, subscribeToPush } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/StatusBadge';
import { formatOrderNo, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import AdminCalendar from '../components/AdminCalendar';
import { 
    ClipboardList, 
    Clock, 
    CheckCircle2, 
    Users, 
    Plus,
    ArrowRight,
    Bell,
    BellOff,
    RefreshCw
} from 'lucide-react';

export const DashboardPage = () => {
    const [stats, setStats] = useState(null);
    const [recentOrders, setRecentOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushLoading, setPushLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        loadData();
        checkPushStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await loadData();
            toast.success('Aktualisiert');
        } catch (err) {
            toast.error('Fehler beim Aktualisieren');
        } finally {
            setRefreshing(false);
        }
    };

    const checkPushStatus = async () => {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            try {
                const registration = await navigator.serviceWorker.ready;
                const subscription = await registration.pushManager.getSubscription();
                setPushEnabled(!!subscription);
            } catch (error) {
                // Silent fail - push check optional
            }
        }
    };

    const enablePushNotifications = async () => {
        setPushLoading(true);
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                toast.error('Benachrichtigungen wurden blockiert. Bitte in den Browser-Einstellungen aktivieren.');
                return;
            }

            const keyRes = await getVapidPublicKey();
            const publicKey = keyRes.data.publicKey;

            const urlBase64ToUint8Array = (base64String) => {
                const padding = '='.repeat((4 - base64String.length % 4) % 4);
                const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
                const rawData = window.atob(base64);
                const outputArray = new Uint8Array(rawData.length);
                for (let i = 0; i < rawData.length; ++i) {
                    outputArray[i] = rawData.charCodeAt(i);
                }
                return outputArray;
            };

            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey)
            });

            await subscribeToPush({
                endpoint: subscription.endpoint,
                keys: {
                    p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
                    auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
                }
            });

            setPushEnabled(true);
            toast.success('Push-Benachrichtigungen aktiviert!');
        } catch (error) {
            // Silent fail - push setup optional
            toast.error('Fehler beim Aktivieren der Benachrichtigungen');
        } finally {
            setPushLoading(false);
        }
    };

    const loadData = async () => {
        try {
            const [statsRes, ordersRes] = await Promise.all([
                getStats(),
                getOrders({ limit: 10 })  // Only load 10 recent orders for dashboard
            ]);
            setStats(statsRes.data);
            setRecentOrders(ordersRes.data);
        } catch (error) {
            // Silent fail
            toast.error('Fehler beim Laden der Dashboard-Daten');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-6" data-testid="dashboard-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                        Dashboard
                        <button 
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="p-2 rounded-full hover:bg-muted transition-colors"
                            title="Aktualisieren"
                        >
                            <RefreshCw size={20} className={`text-muted-foreground ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </h1>
                    <p className="text-muted-foreground">Übersicht Ihrer Aufträge und Mitarbeiter</p>
                </div>
                <div className="flex flex-col gap-2">
                    <Button 
                        variant={pushEnabled ? "outline" : "default"}
                        onClick={enablePushNotifications}
                        disabled={pushLoading || pushEnabled}
                        className={pushEnabled ? "text-primary border-primary" : ""}
                        data-testid="push-notification-btn"
                    >
                        {pushEnabled ? (
                            <>
                                <Bell className="h-4 w-4 mr-2" />
                                Benachrichtigungen aktiv
                            </>
                        ) : (
                            <>
                                <BellOff className="h-4 w-4 mr-2" />
                                {pushLoading ? 'Aktiviere...' : 'Benachrichtigungen aktivieren'}
                            </>
                        )}
                    </Button>
                    <Link to="/orders/new">
                        <Button className="btn-scale w-full" data-testid="new-order-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Neuer Auftrag
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Main Content: Orders, Calendar, and Requests */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Linke Spalte: Aufträge + Anträge (Desktop) */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Alle Aufträge */}
                    <Card className="animate-fade-in">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <ClipboardList className="h-5 w-5 text-primary" />
                        Alle Aufträge
                    </CardTitle>
                    <Link to="/orders">
                        <Button variant="ghost" size="sm" data-testid="view-all-orders-btn">
                            Alle anzeigen
                            <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    </Link>
                </CardHeader>
                <CardContent>
                    {recentOrders.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            Keine Aufträge vorhanden
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {recentOrders.slice(0, 3).map((order) => (
                                <Link 
                                    key={order.id} 
                                    to={`/orders/${order.id}`}
                                    className="block"
                                    data-testid={`order-row-${order.order_no}`}
                                >
                                    <div className="flex items-center justify-between p-4 rounded-sm border bg-card hover:border-primary/30 transition-colors cursor-pointer">
                                        <div className="flex items-center gap-4">
                                            <div>
                                                <span className="order-no text-primary">{formatOrderNo(order.order_no)}</span>
                                                <p className="font-medium mt-1">{order.title}</p>
                                                <p className="text-sm text-muted-foreground">{order.customer?.name}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <StatusBadge status={order.status} />
                                            <div className="mt-2 space-y-0.5">
                                                <p className="text-xs text-muted-foreground">
                                                    Erstellt: {formatDate(order.created_at)}
                                                </p>
                                                {order.planned_date && (
                                                    <p className="text-xs text-primary font-medium">
                                                        Geplant: {formatDate(order.planned_date)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

                    {/* Kalender - NUR auf Mobile hier anzeigen, auf Desktop in rechter Spalte */}
                    <div className="lg:hidden">
                        <AdminCalendar />
                    </div>
                </div>

                {/* Kalender - Rechte Spalte, nur auf Desktop sichtbar (auf Mobile oben nach Aufträgen) */}
                <div className="hidden lg:block lg:col-span-1">
                    <AdminCalendar />
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
